import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Platform,
  Image,
} from "react-native";
import React, { useState, useEffect, useCallback } from "react";
import { Octicons, MaterialIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import Loader from "../../../../components/loader/Loader";
import { router, useFocusEffect } from "expo-router";
import StandardNavigation from "../../../../components/navigation/StandardNavigation";
import Animated, { FadeInDown } from "react-native-reanimated";
import { Toast } from "react-native-toast-notifications";
import i18n from "../../../../locales/i18n";
import customAxios from "../../../../utils/customAxios";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { authActions } from "../../../../store/slices/authSlice";
import { appActions } from "../../../../store/slices/appSlice";
import WebProfileScreen from "../../../../components/web/WebProfileScreen";
import * as ImagePicker from "expo-image-picker";
import * as ImageManipulator from "expo-image-manipulator";
import CustomAlert from "../../../../components/ui/CustomAlert";

const index = () => {
  const dispatch = useDispatch();
  const { user, loading, refetch } = useSelector(
    (state: RootState) => state.auth,
  );
  const { locale } = useSelector((state: RootState) => state.app);

  // State for editable fields
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [isProfilePictureAlertVisible, setProfilePictureAlertVisible] =
    useState(false);

  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);

  // Type assertion for user object based on API response
  const userData = user as {
    id_user?: number;
    username?: string;
    email?: string;
    storename?: string;
    phone?: string;
    location?: string;
    description?: string;
    photo?: string;
    role?: string;
    is_verified?: boolean;
    created_at?: string;
    payed?: boolean; // This is the actual field name from API
    verify_code?: string | null;
    verify_expiry?: string | null;
    categoryId?: number; // Add categoryId field
  } | null;

  useEffect(() => {
    if (userData) {
      setUsername(userData.username || "");
      setPhone(userData.phone || "");
      setEmail(userData.email || "");
    }
  }, [userData]);

  // Fetch categories from API - removed (moved to store-info)

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch]),
  );

  // Fetch user data on mount and when refetch changes
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const res = await customAxios.get("/users/check");
        dispatch(authActions.setUser(res.data.userinfo));
      } catch (error) {
        // silently ignore
      }
    };

    // Fetch on mount or when refetch changes
    fetchUserData();
  }, [dispatch, refetch]);

  // Cancel edit mode and reset form fields
  const handleCancelEdit = () => {
    if (userData) {
      setUsername(userData.username || "");
      setPhone(userData.phone || "");
      setEmail(userData.email || "");
    }
    setIsEditing(false);
  };

  // Handle profile photo pick & upload
  const handleChangePhoto = () => {
    setProfilePictureAlertVisible(true);
  };

  const uploadProfilePhoto = async (uri: string) => {
    setPhotoUploading(true);
    try {
      // Compress image to max 600KB
      let finalUri = uri;
      const response = await fetch(uri);
      const blob = await response.blob();
      const sizeKB = blob.size / 1024;

      if (sizeKB > 600) {
        const compressed = await ImageManipulator.manipulateAsync(
          uri,
          [{ resize: { width: 600 } }],
          { compress: 0.5, format: ImageManipulator.SaveFormat.JPEG },
        );
        finalUri = compressed.uri;
      }

      const formData = new FormData();
      const timestamp = Date.now();
      formData.append("photo", {
        uri: finalUri,
        name: `profile_${timestamp}.jpg`,
        type: "image/jpeg",
      } as any);

      await customAxios.patch("/users/update", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });

      Toast.show(i18n.t("profilePhotoUpdated"), { type: "success" });
      dispatch(authActions.toggleRefetch());
    } catch (error: any) {
      Toast.show(i18n.t("profilePhotoFailed"), { type: "danger" });
    } finally {
      setPhotoUploading(false);
    }
  };

  // Save profile data
  const handleSaveProfile = async () => {
    setSaveLoading(true);
    try {
      // Basic validation
      if (!username.trim()) {
        Toast.show(i18n.t("usernameRequired"), { type: "danger" });
        setSaveLoading(false);
        return;
      }

      // Store name is optional - no validation needed

      // Phone validation (allow 10 digits with optional spaces/dashes)
      if (phone && !/^\d{10}$/.test(phone.replace(/[\s-]/g, ""))) {
        Toast.show(i18n.t("invalidPhoneNumber"), { type: "danger" });
        setSaveLoading(false);
        return;
      }

      // Email validation
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        Toast.show(i18n.t("invalidEmail"), { type: "danger" });
        setSaveLoading(false);
        return;
      }

      // Only include fields that have actually changed
      const updatedFields: any = {};

      // Check if email changed
      const emailChanged = email !== (userData?.email || "");

      if (emailChanged) {
        updatedFields.email = email;
      }

      if (username !== (userData?.username || "")) {
        updatedFields.username = username;
      }

      if (phone !== (userData?.phone || "")) {
        updatedFields.phone = phone;
      }

      // If no fields changed, show a message and return
      if (Object.keys(updatedFields).length === 0) {
        Toast.show(i18n.t("noChangesToSave"), {
          type: "info",
        });
        setSaveLoading(false);
        return;
      }

      const response = await customAxios.patch(`/users/update`, updatedFields, {
        withCredentials: true,
      });

      Toast.show(i18n.t("profileUpdateSuccess"), {
        type: "success",
      });

      // Refetch user data to get updated info
      dispatch(authActions.toggleRefetch());

      // Exit edit mode after successful save
      setIsEditing(false);
    } catch (error: any) {
      // Handle specific error messages
      const errorMessage = error?.response?.data?.message;
      if (errorMessage) {
        Toast.show(errorMessage, { type: "danger" });
      } else {
        Toast.show(i18n.t("profileUpdateFailed"), { type: "danger" });
      }
    } finally {
      setSaveLoading(false);
    }
  };

  // Render web version on web platform (must be before loading check)
  if (Platform.OS === "web") {
    return <WebProfileScreen />;
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
      {/* Standard Navigation */}
      <StandardNavigation
        title={i18n.t("profile")}
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
      />
      {/* Edit/Update Button */}
      <View className="px-5 pt-3 pb-2 bg-[#0596D0]">
        {!isEditing ? (
          <TouchableOpacity
            onPress={() => setIsEditing(true)}
            className="px-4 py-3 rounded-xl flex-row items-center justify-center bg-white"
          >
            <MaterialIcons name="edit" size={20} color="#0596D0" />
            <Text className="font-medium text-base ml-2 text-[#0596D0]">
              {i18n.t("changedata")}
            </Text>
          </TouchableOpacity>
        ) : (
          <View className="flex-row gap-3">
            <TouchableOpacity
              onPress={handleCancelEdit}
              className="flex-1 px-4 py-3 rounded-xl flex-row items-center justify-center bg-gray-500"
            >
              <MaterialIcons name="close" size={20} color="white" />
              <Text className="font-medium text-base ml-2 text-white">
                {i18n.t("cancel")}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handleSaveProfile}
              disabled={saveLoading}
              className="flex-1 px-4 py-3 rounded-xl flex-row items-center justify-center bg-green-500"
            >
              {saveLoading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <>
                  <MaterialIcons name="save" size={20} color="white" />
                  <Text className="font-medium text-base ml-2 text-white">
                    {i18n.t("save")}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Profile Avatar Section */}
      <Animated.View
        entering={FadeInDown.duration(400).delay(100)}
        style={{
          backgroundColor: "#f5f5f5",
          alignItems: "center",
          paddingVertical: 20,
        }}
      >
        <TouchableOpacity
          onPress={handleChangePhoto}
          disabled={photoUploading}
          activeOpacity={0.7}
          style={{ position: "relative" }}
        >
          <View
            style={{
              width: 96,
              height: 96,
              borderRadius: 48,
              backgroundColor: "white",
              alignItems: "center",
              justifyContent: "center",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
          >
            {photoUploading ? (
              <ActivityIndicator size="large" color="#0596D0" />
            ) : userData?.photo ? (
              <Image
                source={{
                  uri: userData.photo.startsWith("http")
                    ? userData.photo
                    : `https://api.khdame.com${userData.photo}`,
                }}
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: 42,
                }}
                resizeMode="cover"
              />
            ) : (
              <View
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: 42,
                  backgroundColor: "#FB923C",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Octicons name="person" size={32} color="white" />
              </View>
            )}
          </View>
          {/* Camera badge */}
          <View
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: "#0596D0",
              alignItems: "center",
              justifyContent: "center",
              borderWidth: 2,
              borderColor: "white",
            }}
          >
            <MaterialIcons name="camera-alt" size={16} color="white" />
          </View>
        </TouchableOpacity>
        <Text
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "#9CA3AF",
          }}
        >
          {i18n.t("changeProfilePhoto")}
        </Text>
      </Animated.View>

      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 16,
          paddingBottom: 100,
        }}
      >
        {/* User Name Field */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(200)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("username")}
          </Text>
          <View
            className={`rounded-xl px-6 py-4  border ${
              isEditing
                ? "bg-white border-gray-100"
                : "bg-gray-50 border-gray-100"
            }`}
          >
            {isEditing ? (
              <TextInput
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#1F2937",
                }}
                value={username}
                onChangeText={setUsername}
                placeholder={i18n.t("defaultUsername")}
                autoCapitalize="none"
              />
            ) : (
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#374151",
                }}
              >
                {username || i18n.t("defaultUsername")}
              </Text>
            )}
          </View>
        </Animated.View>

        {/* Email Field - Editable with verification */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(300)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("email")}
          </Text>
          <View
            className={`rounded-xl px-6 py-4 border relative ${
              isEditing
                ? "bg-white border-blue-400 border-2"
                : "bg-white border-gray-100"
            }`}
          >
            {isEditing ? (
              <TextInput
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#2196F3",
                  fontWeight: "600",
                  paddingRight: locale === "ar" ? 0 : 35,
                  paddingLeft: locale === "ar" ? 35 : 0,
                }}
                value={email}
                onChangeText={setEmail}
                placeholder={i18n.t("enterEmail")}
                keyboardType="email-address"
                autoCapitalize="none"
              />
            ) : (
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: userData?.email ? "#374151" : "#9CA3AF",
                  fontWeight: "500",
                  paddingRight: locale === "ar" ? 0 : 35,
                  paddingLeft: locale === "ar" ? 35 : 0,
                }}
              >
                {userData?.email || i18n.t("notSpecified")}
              </Text>
            )}
            <View
              className={`absolute top-4 ${
                locale === "ar" ? "left-4" : "right-4"
              }`}
            >
              {userData?.is_verified ? (
                <MaterialIcons name="verified" size={22} color="#10B981" />
              ) : (
                <MaterialIcons name="warning" size={22} color="#F59E0B" />
              )}
            </View>
          </View>
          {/* Verification status text */}
          <Text
            className={`text-sm mt-1 ${
              userData?.is_verified ? "text-green-600" : "text-yellow-600"
            } ${locale === "ar" ? "text-right" : "text-left"}`}
          >
            {userData?.is_verified
              ? i18n.t("verified")
              : i18n.t("pendingVerification")}
          </Text>
          {isEditing && (
            <Text
              className={`text-xs mt-1 text-gray-500 ${
                locale === "ar" ? "text-right" : "text-left"
              }`}
            >
              {i18n.t("emailChangeNote") || "Update your email address"}
            </Text>
          )}

          {/* Verify Email Button - Show only if not verified and not editing */}
          {!userData?.is_verified && !isEditing && userData?.email && (
            <TouchableOpacity
              onPress={() => {
                // Store the email for verification and navigate
                dispatch(
                  authActions.setPendingVerificationEmail(userData.email!),
                );
                router.push("/(routes)/(auth)/verify-email");
              }}
              className="mt-3"
            >
              <View className="bg-yellow-50 border border-yellow-300 rounded-xl px-4 py-3 flex-row items-center justify-between">
                <View
                  className={`flex-row items-center ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
                >
                  <MaterialIcons
                    name="mark-email-read"
                    size={20}
                    color="#F59E0B"
                  />
                  <Text
                    className={`text-yellow-700 font-semibold ${
                      locale === "ar" ? "mr-2" : "ml-2"
                    }`}
                  >
                    {i18n.t("verifyEmail")}
                  </Text>
                </View>
                <MaterialIcons
                  name={locale === "ar" ? "chevron-left" : "chevron-right"}
                  size={20}
                  color="#F59E0B"
                />
              </View>
            </TouchableOpacity>
          )}
        </Animated.View>

        {/* Phone Number Field - Highlighted in blue */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(400)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("phone")}
          </Text>
          <View
            className={`rounded-xl px-6 py-4  ${
              isEditing
                ? "bg-white border-2 border-blue-400"
                : "bg-gray-50 border border-gray-200"
            }`}
          >
            {isEditing ? (
              <TextInput
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#2196F3",
                  fontWeight: "600",
                }}
                value={phone}
                onChangeText={setPhone}
                placeholder="0779966025"
                keyboardType="phone-pad"
                autoCapitalize="none"
              />
            ) : (
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: phone ? "#374151" : "#9CA3AF",
                  fontWeight: "500",
                }}
              >
                {phone || i18n.t("notSpecified")}
              </Text>
            )}
          </View>
        </Animated.View>

        {/* Registration Date - Read Only */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(500)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("startDate")}
          </Text>
          <View className="bg-gray-50 rounded-xl px-6 py-4 border border-gray-100">
            <Text
              style={{
                fontSize: 16,
                textAlign: locale === "ar" ? "right" : "left",
                color: "#6B7280",
              }}
            >
              {userData?.created_at
                ? new Date(userData.created_at).toLocaleDateString("en-US")
                : "15/02/2025"}
            </Text>
          </View>
        </Animated.View>

        {/* Subscription Status - Read Only */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(1000)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("subscriptionStatus")}
          </Text>
          <View
            className={`rounded-xl px-6 py-4 ${
              userData?.payed
                ? "bg-green-50 border border-green-200"
                : "bg-yellow-50 border border-yellow-200"
            }`}
          >
            <View
              className={`flex-row items-center ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <MaterialIcons
                name={userData?.payed ? "check-circle" : "access-time"}
                size={22}
                color={userData?.payed ? "#10B981" : "#F59E0B"}
              />
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: userData?.payed ? "#10B981" : "#F59E0B",
                  fontWeight: "600",
                  marginLeft: locale === "ar" ? 0 : 10,
                  marginRight: locale === "ar" ? 10 : 0,
                }}
              >
                {userData?.payed ? i18n.t("activePaid") : i18n.t("free")}
              </Text>
            </View>
          </View>
        </Animated.View>

        {/* Subscription Type - Read Only */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(1100)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("subscriptionType")}
          </Text>
          <View className="bg-white rounded-xl px-6 py-4  border border-gray-100">
            <View
              className={`flex-row items-center ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <MaterialIcons
                name={userData?.payed ? "stars" : "star-border"}
                size={20}
                color={userData?.payed ? "#8B5CF6" : "#6B7280"}
              />
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: userData?.payed ? "#8B5CF6" : "#6B7280",
                  fontWeight: userData?.payed ? "600" : "400",
                  marginLeft: locale === "ar" ? 0 : 8,
                  marginRight: locale === "ar" ? 8 : 0,
                }}
              >
                {userData?.payed ? i18n.t("premium") : i18n.t("free")}
              </Text>
            </View>
          </View>
        </Animated.View>

        {/* Registration Date - Read Only - Only show if exists */}
        {userData?.created_at && (
          <Animated.View
            entering={FadeInDown.duration(400).delay(1200)}
            className="mb-6"
          >
            <Text
              className={`text-gray-600 text-base mb-2 ${
                locale === "ar" ? "text-right" : "text-left"
              }`}
            >
              {i18n.t("registrationDate")}
            </Text>
            <View className="bg-white rounded-xl px-6 py-4  border border-gray-100">
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#374151",
                }}
              >
                {new Date(userData.created_at).toLocaleDateString("en-US")}
              </Text>
            </View>
          </Animated.View>
        )}

        {/* Upgrade Subscription - Show only for non-paid users */}
        {!userData?.payed && (
          <Animated.View entering={FadeInDown.duration(400).delay(1300)}>
            <TouchableOpacity
              onPress={() => {
                // Navigate to contact page for upgrade assistance
                router.push("/(drawer)/params/contact?upgrade=true");
              }}
              className="mb-6"
            >
              <LinearGradient
                colors={["#8B5CF6", "#7C3AED"]}
                style={{
                  borderRadius: 20,
                  paddingVertical: 18,
                  paddingHorizontal: 24,
                  shadowColor: "#8B5CF6",
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.3,
                  shadowRadius: 8,
                  elevation: 8,
                }}
              >
                <View
                  className={`flex-row items-center justify-between ${
                    locale === "ar" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <View
                    className={`flex-row items-center ${
                      locale === "ar" ? "flex-row-reverse" : "flex-row"
                    }`}
                  >
                    <MaterialIcons name="stars" size={26} color="white" />
                    <Text
                      className="text-white text-base font-semibold"
                      style={{
                        marginLeft: locale === "ar" ? 0 : 12,
                        marginRight: locale === "ar" ? 12 : 0,
                      }}
                    >
                      {i18n.t("upgradeToPremium")}
                    </Text>
                  </View>
                  <MaterialIcons
                    name={locale === "ar" ? "chevron-left" : "chevron-right"}
                    size={24}
                    color="white"
                  />
                </View>
              </LinearGradient>
            </TouchableOpacity>
          </Animated.View>
        )}

        {/* Payment History Link - Disabled with Low Opacity */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(1400)}
          className="mb-6"
          style={{ opacity: 0.5 }}
        >
          <View className="rounded-xl px-6 py-5 border bg-gray-50 border-gray-100">
            <View
              className={`flex-row items-center justify-between ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <View
                className={`flex-row items-center ${
                  locale === "ar" ? "flex-row-reverse" : "flex-row"
                }`}
              >
                <View className="p-2 rounded-full bg-gray-100">
                  <MaterialIcons name="payment" size={22} color="#9CA3AF" />
                </View>
                <View
                  style={{
                    marginLeft: locale === "ar" ? 0 : 12,
                    marginRight: locale === "ar" ? 12 : 0,
                  }}
                >
                  <Text className="text-base font-medium text-gray-400">
                    {i18n.t("paymentHistory")}
                  </Text>
                  <Text className="text-sm mt-1 text-gray-400">
                    {i18n.t("currentlyUnavailable")}
                  </Text>
                </View>
              </View>
              <MaterialIcons name="lock" size={20} color="#9CA3AF" />
            </View>
          </View>
          <Text
            className={`text-xs mt-2 text-gray-400 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("featureUnderDevelopment")}
          </Text>
        </Animated.View>

        {/* View Limits Link */}
        <Animated.View entering={FadeInDown.duration(400).delay(1500)}>
          <TouchableOpacity
            onPress={() => router.push("/(drawer)/params/limits")}
            className="mb-6"
          >
            <View
              className={`flex-row items-center justify-between ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <View
                className={`flex-row items-center ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
              >
                <MaterialIcons name="bar-chart" size={20} color="#3B82F6" />
                <Text
                  className={`text-gray-600 text-base ${locale === "ar" ? "mr-2" : "ml-2"}`}
                >
                  {i18n.t("viewLimits")}
                </Text>
              </View>
              <MaterialIcons
                name={locale === "ar" ? "chevron-left" : "chevron-right"}
                size={24}
                color="#6B7280"
              />
            </View>
          </TouchableOpacity>
        </Animated.View>

        {/* Change Password Link */}
        <Animated.View entering={FadeInDown.duration(400).delay(1600)}>
          <TouchableOpacity
            onPress={() =>
              router.push("/(drawer)/params/profile/change-password")
            }
            className="mb-8"
          >
            <View
              className={`flex-row items-center justify-between ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <Text className="text-gray-600 text-base">
                {i18n.t("changePassword")}
              </Text>
              <MaterialIcons
                name={locale === "ar" ? "chevron-left" : "chevron-right"}
                size={24}
                color="#6B7280"
              />
            </View>
          </TouchableOpacity>
        </Animated.View>
      </ScrollView>

      <CustomAlert
        visible={isProfilePictureAlertVisible}
        title={i18n.t("changeProfilePhoto")}
        message={i18n.t("chooseOption")}
        icon="camera"
        onDismiss={() => setProfilePictureAlertVisible(false)}
        buttons={[
          {
            text: i18n.t("takePhoto"),
            onPress: async () => {
              setProfilePictureAlertVisible(false);
              const perm = await ImagePicker.requestCameraPermissionsAsync();
              if (!perm.granted) return;
              const result = await ImagePicker.launchCameraAsync({
                allowsEditing: true,
                aspect: [1, 1],
                quality: 0.8,
              });
              if (!result.canceled) {
                await uploadProfilePhoto(result.assets[0].uri);
              }
            },
          },
          {
            text: i18n.t("chooseFromGallery"),
            onPress: async () => {
              setProfilePictureAlertVisible(false);
              const perm =
                await ImagePicker.requestMediaLibraryPermissionsAsync();
              if (!perm.granted) return;
              const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ["images"],
                allowsEditing: true,
                aspect: [1, 1],
                quality: 0.8,
              });
              if (!result.canceled) {
                await uploadProfilePhoto(result.assets[0].uri);
              }
            },
          },
          {
            text: i18n.t("cancel"),
            style: "cancel",
            onPress: () => setProfilePictureAlertVisible(false),
          },
        ]}
      />
    </View>
  );
};

export default index;
